/**
 * GraphQL queries & mutations for the Shopify Storefront API.
 *
 * All operations that depend on market context declare
 * `@inContext(country: $country, language: $language)`. The storefront client
 * detects the `$country` / `$language` variables and injects them
 * automatically (see `client.ts`).
 */

// ---------------------------------------------------------------------------
//  Fragments
// ---------------------------------------------------------------------------

const MONEY_FRAGMENT = /* GraphQL */ `
  fragment MoneyFields on MoneyV2 {
    amount
    currencyCode
  }
`;

const IMAGE_FRAGMENT = /* GraphQL */ `
  fragment ImageFields on Image {
    id
    url
    altText
    width
    height
  }
`;

const VARIANT_FRAGMENT = /* GraphQL */ `
  fragment VariantFields on ProductVariant {
    id
    title
    availableForSale
    quantityAvailable
    sku
    barcode
    price { ...MoneyFields }
    compareAtPrice { ...MoneyFields }
    selectedOptions { name value }
    image { ...ImageFields }
  }
`;

const PRODUCT_FRAGMENT = /* GraphQL */ `
  fragment ProductFields on Product {
    id
    handle
    title
    description
    descriptionHtml
    vendor
    productType
    tags
    seo { title description }
    images(first: 20) {
      nodes { ...ImageFields }
    }
    options(first: 3) {
      id
      name
      values
    }
    variants(first: 100) {
      nodes { ...VariantFields }
    }
  }
`;

const CART_FRAGMENT = /* GraphQL */ `
  fragment CartFields on Cart {
    id
    checkoutUrl
    totalQuantity
    createdAt
    updatedAt
    note
    attributes { key value }
    discountCodes { code applicable }
    discountAllocations {
      ... on CartCodeDiscountAllocation {
        targetType
        code
        discountedAmount { ...MoneyFields }
      }
      ... on CartAutomaticDiscountAllocation {
        targetType
        title
        discountedAmount { ...MoneyFields }
      }
    }
    buyerIdentity {
      countryCode
      email
      phone
      customer { id email }
    }
    cost {
      totalAmount { ...MoneyFields }
      subtotalAmount { ...MoneyFields }
      totalTaxAmount { ...MoneyFields }
      totalDutyAmount { ...MoneyFields }
    }
    lines(first: 50) {
      nodes {
        id
        quantity
        attributes { key value }
        cost {
          totalAmount { ...MoneyFields }
          subtotalAmount { ...MoneyFields }
          amountPerQuantity { ...MoneyFields }
          compareAtAmountPerQuantity { ...MoneyFields }
        }
        merchandise {
          ... on ProductVariant {
            id
            title
            sku
            availableForSale
            quantityAvailable
            selectedOptions { name value }
            image { ...ImageFields }
            product {
              id
              handle
              title
              vendor
              featuredImage { ...ImageFields }
            }
          }
        }
      }
    }
  }
`;

// ---------------------------------------------------------------------------
//  Queries
// ---------------------------------------------------------------------------

export const PRODUCT_BY_HANDLE = /* GraphQL */ `
  query ProductByHandle($handle: String!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    product(handle: $handle) {
      ...ProductFields
    }
  }
  ${PRODUCT_FRAGMENT}
  ${VARIANT_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;

/**
 * Related products : Shopify built-in productRecommendations (similar products
 * via collaborative filtering). Les 10 premiers résultats suffisent.
 */
export const PRODUCT_RECOMMENDATIONS = /* GraphQL */ `
  query Recs($productId: ID!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    productRecommendations(productId: $productId) {
      handle
    }
  }
`;

/**
 * Fetch N produits par handles — utilisé pour last-viewed (localStorage) et
 * pour le rendu des clones de smootify-product[data-id="related|last-viewed"].
 */
export const PRODUCTS_BY_HANDLES = /* GraphQL */ `
  query ProductsByHandles($query: String!, $first: Int!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    products(first: $first, query: $query) {
      nodes {
        handle
      }
    }
  }
`;

/**
 * Fetch N produits FULL (avec variants/images/options) par handles via 1 seule
 * query batch. Utilisé pour la page filter au boot pour hydrater 40 cards en
 * 1 fetch au lieu de 40. Gain perf majeur.
 *
 * `query` Shopify : "handle:A OR handle:B OR ..." (max 250 produits/page).
 */
export const PRODUCTS_FULL_BY_HANDLES = /* GraphQL */ `
  query ProductsFullByHandles($query: String!, $first: Int!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    products(first: $first, query: $query) {
      nodes {
        ...ProductFields
      }
    }
  }
  ${PRODUCT_FRAGMENT}
  ${VARIANT_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;

/**
 * Fetch N produits LIGHT (juste les fields necessaires pour rendre une CARD)
 * par handles via 1 seule query batch. Utilise par hydrateBatchEager au boot
 * du catalogue.
 *
 * Diff vs PRODUCTS_FULL_BY_HANDLES :
 *   - Pas de descriptionHtml (les cards n'affichent pas la description)
 *   - Pas de seo (pas affiche sur cards)
 *   - Pas d'options (pas de variant selector sur cards)
 *   - images limit 1 (juste featured pour le rendu de la card)
 *   - variants limit 6 (pour les vignettes thumbs hover) sans sku/barcode/qty
 *
 * Mesure : LIGHT = 73KB / 600ms vs FULL = 257KB / 1000ms (curl direct).
 * En browser : -400ms a -1s sur le slowest call critique.
 */
export const PRODUCTS_LIGHT_BY_HANDLES = /* GraphQL */ `
  query ProductsLightByHandles($query: String!, $first: Int!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    products(first: $first, query: $query) {
      nodes {
        id
        handle
        title
        vendor
        productType
        tags
        featuredImage { ...ImageFields }
        priceRange {
          minVariantPrice { ...MoneyFields }
          maxVariantPrice { ...MoneyFields }
        }
        variants(first: 6) {
          nodes {
            id
            availableForSale
            price { ...MoneyFields }
            image { ...ImageFields }
            selectedOptions { name value }
          }
        }
      }
    }
  }
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;

/**
 * Shopify Predictive Search (autocomplete dropdown).
 * Retourne les top 10 résultats (produits + queries suggérées) — cap fixe
 * imposé par Shopify, pas configurable.
 *
 * Usage : au fil de la frappe utilisateur, fetch avec debounce 200ms.
 *
 * Les résultats sont limités à 10 tous types confondus. Shopify renvoie
 * séparément products, collections, pages, queries.
 */
export const PREDICTIVE_SEARCH = /* GraphQL */ `
  query PredictiveSearch($query: String!, $limit: Int, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    predictiveSearch(query: $query, limit: $limit, types: [PRODUCT, QUERY]) {
      products {
        id
        handle
        title
        vendor
        featuredImage { url altText }
        priceRange {
          minVariantPrice { ...MoneyFields }
        }
      }
      queries {
        text
        styledText
      }
    }
  }
  ${MONEY_FRAGMENT}
`;

/**
 * Fetch tous les produits de la boutique (jusqu'à 250 par page) pour
 * alimenter les pages catalogue / filter (`<smootify-product data-id="filter">`).
 * Retourne les handles + quelques champs utiles pour le filtrage client-side
 * (prix min/max, tags) sans payload trop lourd.
 */
/**
 * Localization Shopify : countries supportés (avec currency + langues)
 * + country et langue actuels résolus par @inContext.
 * Pas de scope nécessaire — public via Storefront token.
 */
export const LOCALIZATION = /* GraphQL */ `
  query Localization($country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    localization {
      country { isoCode name currency { isoCode symbol } }
      language { isoCode name endonymName }
      availableCountries {
        isoCode
        name
        unitSystem
        currency { isoCode symbol }
        availableLanguages { isoCode name endonymName }
      }
    }
  }
`;

export const ALL_PRODUCTS = /* GraphQL */ `
  query AllProducts($first: Int!, $after: String, $query: String, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    products(first: $first, after: $after, query: $query) {
      pageInfo { hasNextPage endCursor }
      nodes {
        handle
        title
        vendor
        priceRange {
          minVariantPrice { ...MoneyFields }
          maxVariantPrice { ...MoneyFields }
        }
      }
    }
  }
  ${MONEY_FRAGMENT}
`;

export const CART_QUERY = /* GraphQL */ `
  query Cart($cartId: ID!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    cart(id: $cartId) {
      ...CartFields
    }
  }
  ${CART_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;

// ---------------------------------------------------------------------------
//  Mutations
// ---------------------------------------------------------------------------

export const CART_CREATE = /* GraphQL */ `
  mutation CartCreate($input: CartInput!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    cartCreate(input: $input) {
      cart { ...CartFields }
      userErrors { field message code }
    }
  }
  ${CART_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;

export const CART_LINES_ADD = /* GraphQL */ `
  mutation CartLinesAdd(
    $cartId: ID!
    $lines: [CartLineInput!]!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart { ...CartFields }
      userErrors { field message code }
    }
  }
  ${CART_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;

export const CART_LINES_UPDATE = /* GraphQL */ `
  mutation CartLinesUpdate(
    $cartId: ID!
    $lines: [CartLineUpdateInput!]!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { ...CartFields }
      userErrors { field message code }
    }
  }
  ${CART_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;

export const CART_LINES_REMOVE = /* GraphQL */ `
  mutation CartLinesRemove(
    $cartId: ID!
    $lineIds: [ID!]!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { ...CartFields }
      userErrors { field message code }
    }
  }
  ${CART_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;

export const CART_DISCOUNT_CODES_UPDATE = /* GraphQL */ `
  mutation CartDiscountCodesUpdate(
    $cartId: ID!
    $discountCodes: [String!]!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart { ...CartFields }
      userErrors { field message code }
    }
  }
  ${CART_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;

export const CART_BUYER_IDENTITY_UPDATE = /* GraphQL */ `
  mutation CartBuyerIdentityUpdate(
    $cartId: ID!
    $buyerIdentity: CartBuyerIdentityInput!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart { ...CartFields }
      userErrors { field message code }
    }
  }
  ${CART_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;

export const CART_NOTE_UPDATE = /* GraphQL */ `
  mutation CartNoteUpdate(
    $cartId: ID!
    $note: String!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    cartNoteUpdate(cartId: $cartId, note: $note) {
      cart { ...CartFields }
      userErrors { field message code }
    }
  }
  ${CART_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${MONEY_FRAGMENT}
`;
